<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcAccountHeader',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'deleted']);

  const props = defineProps({
    account: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    resourceTitle: $fn.value.pgettext('Account', 'Account'),
    newAccount: $fn.value.pgettext('Account', 'New account'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    if (loaded.value) {
      return messages.value.resourceTitle;
    }
    return null;
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return props.account.isNew
        ? messages.value.newAccount
        : props.account.snapshot('name');
    }
    return null;
  });

  watch(
    () => props.account,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('account');
    }
  );

  const act_6917646200545441_emit_biqkvwym7_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_997556510679958_emit_psqc5u7ku_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };

  const act_6917646200545441_emit_biqkvwym7 = new actionTypes.EmitAction({
    actionArgs: act_6917646200545441_emit_biqkvwym7_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_6917646200545441_clickPreventStop_bg2gqrike =
    new actionTypes.EventHandlerImpl({
      action: act_6917646200545441_emit_biqkvwym7,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_6917646200545441_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6917646200545441_clickPreventStop_bg2gqrike],
      event: 'click.prevent.stop',
    });
  const act_997556510679958_emit_psqc5u7ku = new actionTypes.EmitAction({
    actionArgs: act_997556510679958_emit_psqc5u7ku_getActionArgs,
  });
  const inst_evh_997556510679958_success_xfrxz9388 =
    new actionTypes.EventHandlerImpl({
      action: act_997556510679958_emit_psqc5u7ku,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_997556510679958_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_997556510679958_success_xfrxz9388],
    event: 'success',
  });

  const actions = {
    inst_evh_6917646200545441_clickPreventStop_bg2gqrike,
    reload: inst_evh_6917646200545441_clickPreventStop_bg2gqrike,
    inst_evh_6917646200545441_clickPreventStop,
    inst_evh_997556510679958_success_xfrxz9388,
    inst_evh_997556510679958_success,
  };

  const evh_6917646200545441_clickPreventStop_bg2gqrike = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6917646200545441_clickPreventStop_bg2gqrike.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6917646200545441_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6917646200545441_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_997556510679958_success_xfrxz9388 = async (event, scope) => {
    return await actions.inst_evh_997556510679958_success_xfrxz9388.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_997556510679958_success = async (event, scope) => {
    return await actions.inst_evh_997556510679958_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpText tag="h5" capitalize muted class="mb-0">
      {{ resourceTitle }}
    </ItpText>
    <span class="m-0 p-0"> &middot; </span>
    <ItpText tag="h4" :text="resourceKey" class="mb-0"> </ItpText>
    <div class="btn-toolbar ms-auto flex-nowrap gap-2">
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        v-if="!account.isNew"
        @click.prevent.stop="evh_6917646200545441_clickPreventStop($event, {})"
      >
      </ItpButton>
      <BDropdown right variant="light" class="border" v-if="!account.isNew">
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="account"
          :tooltip="pgettext('Account', 'Delete account')"
          v-if="!account.isNew || !account.isReadOnly"
          @success="evh_997556510679958_success($event, {})"
        >
        </BtnDeleteResource>
      </BDropdown>
    </div>
  </ItpBox>
</template>
